import { MeterProvider } from '@opentelemetry/sdk-metrics';
import type { Histogram, Meter, MetricAttributes } from '@opentelemetry/api';
import type { AuthStore } from 'app/$store.types';
import { JsonMetricExporter } from './uiMetricsExporter';

type MetricsAuthStore = Partial<Pick<AuthStore, 'companyId' | 'getActiveUserProperty'>>;

let authStore: MetricsAuthStore | undefined;
let accountDeviceCount: number | undefined;

// Measurements taken before initializeMetrics() (e.g. AppWrapper's first render pass) are
// buffered and flushed on init. Normal sessions buffer 1-2; the cap only guards against a leak
// on pages that never initialize metrics (login).
const MAX_PENDING_RECORDS = 100;
let pendingRecords: Array<() => void> = [];

function bufferPendingRecord(flush: () => void) {
  if (pendingRecords.length < MAX_PENDING_RECORDS) {
    pendingRecords.push(flush);
  }
}

// account_scale is the low-cardinality cohort label every browser metric carries so dashboards
// can slice by account size without per-company labels (which explode series cardinality).
export function getAccountScale(deviceCount?: number) {
  if (!Number.isFinite(deviceCount) || (deviceCount as number) < 0) {
    return 'unknown';
  }
  const count = deviceCount as number;
  if (count < 100) {
    return 'lt100';
  }
  if (count < 1000) {
    return 'lt1k';
  }
  if (count < 10000) {
    return 'lt10k';
  }
  if (count < 50000) {
    return 'lt50k';
  }
  return 'gte50k';
}

// Set by $devices once device statistics load; account_scale is 'unknown' until then.
export function setAccountDeviceCount(count?: number) {
  accountDeviceCount = count;
}

function getCommonAttributes(): MetricAttributes {
  return { account_scale: getAccountScale(accountDeviceCount) };
}

// Active (possibly spoofed) company for includeCid labels: $auth.companyId resolves through
// chfAdmin during spoofed sessions (the admin's own company), which is the wrong company for
// usage metrics — mirror the tracing identity convention instead.
function getCompanyIdLabel(): string | number | undefined {
  const active = authStore?.getActiveUserProperty?.('company_id');
  if (typeof active === 'string' || typeof active === 'number') {
    return active;
  }
  return authStore?.companyId;
}

export interface InstrumentOptions {
  name: string;
  description: string;
  includeCid?: boolean;
}

function validate({ name, description }: Partial<InstrumentOptions>) {
  if (!name || !description) {
    throw new Error('Validation Error: missing name or description');
  }
}

class MeterProxy {
  meter: Meter;

  prefix: string;

  constructor({ meter, prefix }: { meter: Meter; prefix: string }) {
    this.meter = meter;
    this.prefix = prefix;
  }

  getHistogram({ name, description }: InstrumentOptions) {
    return this.meter.createHistogram(`${this.prefix}${name}`, { description });
  }

  getCounter({ name, description }: InstrumentOptions) {
    return this.meter.createCounter(`${this.prefix}${name}`, { description });
  }

  getObservableGauge({ name, description }: InstrumentOptions) {
    return this.meter.createObservableGauge(`${this.prefix}${name}`, { description });
  }
}

let oneMeter: MeterProxy | undefined;

// for repeated start/stop with the same object.
// Binds to the underlying otel instrument lazily so instruments created before
// initializeMetrics() (class fields, module scope) still record once metrics are up.
export class HistogramProxy {
  private readonly _options: Partial<InstrumentOptions>;

  private _startTime: number;

  private _histogram?: Histogram;

  constructor(options: Partial<InstrumentOptions> = {}) {
    this._options = options;
    this._startTime = Date.now();
  }

  get histogram(): Histogram | undefined {
    if (!this._histogram && oneMeter && this._options.name) {
      this._histogram = oneMeter.getHistogram(this._options as InstrumentOptions);
    }
    return this._histogram;
  }

  start() {
    this._startTime = Date.now();
  }

  record(value: number, attributes: MetricAttributes = {}) {
    if (!oneMeter) {
      bufferPendingRecord(() => this.record(value, attributes));
      return;
    }
    const { histogram } = this;
    if (histogram) {
      const labels: MetricAttributes = { ...getCommonAttributes(), ...attributes };
      if (this._options.includeCid) {
        labels.company_id = getCompanyIdLabel();
      }
      histogram.record(value, labels);
    }
  }

  end(attributes: MetricAttributes = {}) {
    if (this._startTime > 0) {
      this.record((Date.now() - this._startTime) / 1000, attributes);
    }
    this._startTime = 0;
  }
}

export function createHistogram(options: InstrumentOptions) {
  validate(options);
  return new HistogramProxy(options);
}

export function createCounter(options: InstrumentOptions) {
  validate(options);
  let counter: ReturnType<Meter['createCounter']> | undefined;
  const add = (attributes: MetricAttributes = {}, v = 1) => {
    if (!oneMeter) {
      bufferPendingRecord(() => add(attributes, v));
      return;
    }
    if (!counter) {
      counter = oneMeter.getCounter(options);
    }
    const labels: MetricAttributes = { ...getCommonAttributes(), ...attributes };
    if (options.includeCid) {
      labels.company_id = getCompanyIdLabel();
    }
    counter.add(v, labels);
  };
  return add;
}

type GaugeObservation = number | { value: number; attributes?: MetricAttributes };

/**
 * Registers an observable gauge whose callback runs at every metric collection (the exporter's
 * 10s tick). `observe` may return a number, `{ value, attributes }`, an array of either, or
 * null/undefined to skip the observation.
 */
export function createObservableGauge(
  options: InstrumentOptions,
  observe: () => GaugeObservation | GaugeObservation[] | null | undefined
) {
  validate(options);
  let registered = false;
  const register = () => {
    if (registered || !oneMeter) {
      return;
    }
    registered = true;
    oneMeter.getObservableGauge(options).addCallback((result) => {
      const observation = observe();
      const observations = Array.isArray(observation) ? observation : [observation];
      observations.forEach((entry) => {
        if (entry == null) {
          return;
        }
        const { value, attributes = {} } = typeof entry === 'number' ? { value: entry } : entry;
        if (!Number.isFinite(value)) {
          return;
        }
        const labels: MetricAttributes = { ...getCommonAttributes(), ...attributes };
        if (options.includeCid) {
          labels.company_id = getCompanyIdLabel();
        }
        result.observe(value, labels);
      });
    });
  };
  register();
  if (!registered) {
    bufferPendingRecord(register);
  }
}

export function initializeMetrics(options: { prefix?: string; $auth?: MetricsAuthStore } = {}) {
  if (!oneMeter) {
    const meterProvider = new MeterProvider();
    const reader = new JsonMetricExporter({});
    meterProvider.addMetricReader(reader);
    const meter = meterProvider.getMeter('uiBrowser');
    oneMeter = new MeterProxy({ prefix: options.prefix || 'uiBrowser_', meter });
  }
  authStore = options.$auth;

  const pending = pendingRecords;
  pendingRecords = [];
  pending.forEach((flush) => flush());

  return oneMeter;
}
